/**
 * Static demo data for the Vision Zero Network Tab.
 * This replaces the mock backend (port 3002) that was never deployed.
 */

export const partnerHospitals = {
  charite_berlin: {
    hospital_info: {
      id: 'charite_berlin',
      name: 'Charité - Universitätsmedizin Berlin',
      country: 'Germany',
      location: {
        lat: 52.5244,
        lng: 13.3777,
        address: 'Charitéplatz 1, 10117 Berlin'
      },
      network_status: 'vision_zero_ready',
      argus_partner_since: '2019',
      annual_patients: 142000,
      specializations: ['oncology', 'cardiology', 'neurology', 'surgery']
    },
    capabilities: {
      data_volume: { total_patients: 142000, cancer_patients: 12500, annual_new_cases: 3200, follow_up_years: 8 },
      research_infrastructure: { clinical_trials_active: 156, research_staff: 340, biobank_samples: 85000, imaging_studies_annual: 45000 },
      technical_readiness: { argus_integration: 'full', fhir_compliance: 'r4_ready', gold_compatibility_score: 0.94, data_quality_score: 0.89 }
    },
    network_contributions: { shared_protocols: 23, collaborative_studies: 8, data_sharing_agreements: 12, joint_publications: 45 },
    revenue_potential: { current_annual_research_revenue: 300000, network_enhanced_projection: 1200000, premium_reports_potential: 850000, consortium_membership_value: 400000 }
  },
  hamburg_uke: {
    hospital_info: {
      id: 'hamburg_uke',
      name: 'Universitätsklinikum Hamburg-Eppendorf',
      country: 'Germany',
      location: {
        lat: 53.5891,
        lng: 9.9765,
        address: 'Martinistraße 52, 20246 Hamburg'
      },
      network_status: 'vision_zero_ready',
      argus_partner_since: '2020',
      annual_patients: 89000,
      specializations: ['oncology', 'hematology', 'transplantation', 'cardiology']
    },
    capabilities: {
      data_volume: { total_patients: 89000, cancer_patients: 8900, annual_new_cases: 2100, follow_up_years: 6 },
      research_infrastructure: { clinical_trials_active: 98, research_staff: 210, biobank_samples: 52000, imaging_studies_annual: 28000 },
      technical_readiness: { argus_integration: 'full', fhir_compliance: 'r4_ready', gold_compatibility_score: 0.96, data_quality_score: 0.92 }
    },
    network_contributions: { shared_protocols: 18, collaborative_studies: 6, data_sharing_agreements: 9, joint_publications: 32 },
    revenue_potential: { current_annual_research_revenue: 250000, network_enhanced_projection: 980000, premium_reports_potential: 720000, consortium_membership_value: 350000 }
  },
  munich_klinikum: {
    hospital_info: {
      id: 'munich_klinikum',
      name: 'Klinikum der Universität München',
      country: 'Germany',
      location: {
        lat: 48.1109,
        lng: 11.4695,
        address: 'Marchioninistraße 15, 81377 München'
      },
      network_status: 'integration_pending',
      argus_partner_since: '2021',
      annual_patients: 67000,
      specializations: ['oncology', 'pediatrics', 'neurology', 'genetics']
    },
    capabilities: {
      data_volume: { total_patients: 67000, cancer_patients: 6200, annual_new_cases: 1800, follow_up_years: 5 },
      research_infrastructure: { clinical_trials_active: 72, research_staff: 180, biobank_samples: 38000, imaging_studies_annual: 22000 },
      technical_readiness: { argus_integration: 'partial', fhir_compliance: 'r4_ready', gold_compatibility_score: 0.91, data_quality_score: 0.85 }
    },
    network_contributions: { shared_protocols: 12, collaborative_studies: 4, data_sharing_agreements: 7, joint_publications: 19 },
    revenue_potential: { current_annual_research_revenue: 180000, network_enhanced_projection: 750000, premium_reports_potential: 540000, consortium_membership_value: 280000 }
  }
};

export const collaborationsData = {
  multi_site_opportunities: [
    {
      opportunity_id: 'onco_car_t_2024',
      title: 'CAR-T Cell Therapy Outcomes Study',
      description: 'Multi-center analysis of CAR-T therapy effectiveness across DACH partner hospitals',
      required_specialization: ['oncology', 'hematology'],
      minimum_patient_volume: 200,
      study_duration_months: 24,
      estimated_revenue_per_site: 180000,
      vision_zero_aligned: true,
      matching_partners: [
        { hospital_id: 'hamburg_uke', hospital_name: 'UKE Hamburg', match_score: 0.96, patient_volume: 310, special_capabilities: ['advanced_car_t_center', 'long_term_followup'] },
        { hospital_id: 'munich_klinikum', hospital_name: 'Klinikum München', match_score: 0.91, patient_volume: 280, special_capabilities: ['pediatric_car_t', 'biomarker_analysis'] }
      ]
    },
    {
      opportunity_id: 'cardio_toxicity_2024',
      title: 'Cancer Therapy Cardiotoxicity Prevention',
      description: 'Early detection of cardiac complications from cancer treatment using AI-enhanced monitoring',
      required_specialization: ['oncology', 'cardiology'],
      minimum_patient_volume: 500,
      study_duration_months: 36,
      estimated_revenue_per_site: 240000,
      vision_zero_aligned: true,
      gold_profile_required: 'cardiovascular_bundle_v2_1',
      matching_partners: [
        { hospital_id: 'charite_berlin', hospital_name: 'Charité Berlin', match_score: 0.98, patient_volume: 820, special_capabilities: ['cardiac_mri', 'biomarker_lab', 'ai_analysis'] }
      ]
    },
    {
      opportunity_id: 'neuro_biomarker_2024',
      title: 'Neurological Biomarker Discovery Network',
      description: 'Cross-center identification of novel biomarkers for neurodegenerative diseases',
      required_specialization: ['neurology', 'genetics'],
      minimum_patient_volume: 300,
      study_duration_months: 30,
      estimated_revenue_per_site: 160000,
      vision_zero_aligned: true,
      matching_partners: [
        { hospital_id: 'charite_berlin', hospital_name: 'Charité Berlin', match_score: 0.93, patient_volume: 450, special_capabilities: ['neuroimaging', 'genetic_sequencing'] },
        { hospital_id: 'munich_klinikum', hospital_name: 'Klinikum München', match_score: 0.89, patient_volume: 320, special_capabilities: ['pediatric_neurology', 'genetics_lab'] }
      ]
    }
  ]
};

export const revenueProjections = {
  baseline_scenario: {
    description: 'Current ARGUS operations without Vision Zero',
    annual_revenue_per_hospital: 300000,
    total_network_revenue: 1500000,
    growth_rate_annual: 0.08
  },
  vision_zero_scenario: {
    description: 'Full Vision Zero Consortium activation',
    total_network_revenue: 6300000,
    roi_timeline: { months_to_breakeven: 4, year_1_return: 3.2 }
  }
};

// Simplified GeoJSON outlines for Germany and Luxembourg
export const germanyGeoData = {
  features: [{
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [6.0, 47.3], [6.2, 47.4], [6.5, 47.5], [7.0, 47.5], [7.5, 47.6],
        [7.7, 47.5], [8.0, 47.5], [8.5, 47.6], [8.7, 47.7], [9.5, 47.5],
        [9.8, 47.5], [10.1, 47.3], [10.5, 47.4], [10.9, 47.3], [11.4, 47.4],
        [12.0, 47.4], [12.2, 47.6], [12.7, 47.6], [13.0, 47.5], [13.1, 47.7],
        [12.9, 47.9], [13.0, 48.3], [13.4, 48.6], [13.7, 48.5], [13.8, 48.7],
        [14.0, 48.6], [13.8, 48.8], [14.7, 48.6], [15.0, 49.0], [14.3, 49.2],
        [14.6, 49.6], [14.3, 50.0], [14.8, 50.3], [14.6, 50.8], [15.0, 51.0],
        [14.8, 51.1], [14.9, 51.5], [14.7, 51.9], [14.2, 52.0], [14.1, 52.4],
        [14.6, 52.6], [14.5, 52.8], [14.1, 53.0], [14.2, 53.3], [14.0, 53.4],
        [13.5, 53.9], [13.2, 54.1], [12.9, 54.2], [12.4, 54.4], [12.0, 54.5],
        [11.5, 54.3], [11.0, 54.4], [10.8, 54.7], [10.3, 54.6], [9.9, 54.8],
        [9.4, 54.8], [8.7, 54.9], [8.6, 54.6], [9.0, 54.5], [8.6, 54.2],
        [8.4, 54.2], [8.6, 53.9], [8.2, 53.7], [8.5, 53.6], [8.1, 53.5],
        [7.3, 53.6], [7.1, 53.3], [7.0, 53.0], [6.7, 52.6], [7.1, 52.4],
        [6.8, 52.1], [7.0, 51.9], [6.7, 51.7], [6.2, 51.8], [6.0, 51.8],
        [5.9, 51.0], [6.0, 50.7], [6.2, 50.4], [6.1, 50.1], [6.4, 50.0],
        [6.1, 49.5], [6.5, 49.4], [6.3, 49.2], [6.6, 49.0], [6.2, 48.7],
        [6.4, 48.5], [6.8, 48.1], [7.0, 47.9], [7.5, 47.6], [7.6, 47.6],
        [7.5, 47.5], [7.0, 47.5], [6.5, 47.5], [6.0, 47.3]
      ]]
    }
  }]
};

export const luxembourgGeoData = {
  features: [{
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [5.73, 49.55], [5.77, 49.65], [5.82, 49.72], [5.98, 49.80],
        [6.03, 49.88], [6.13, 49.95], [6.20, 50.00], [6.40, 50.08],
        [6.52, 49.96], [6.53, 49.83], [6.56, 49.73], [6.50, 49.63],
        [6.52, 49.50], [6.45, 49.45], [6.35, 49.46], [6.25, 49.50],
        [6.15, 49.48], [6.02, 49.45], [5.85, 49.47], [5.73, 49.55]
      ]]
    }
  }]
};

export const citiesData = {
  cities: [
    { name: 'Berlin', lat: 52.52, lng: 13.405 },
    { name: 'Hamburg', lat: 53.551, lng: 9.994 },
    { name: 'München', lat: 48.137, lng: 11.576 },
    { name: 'Köln', lat: 50.937, lng: 6.960 },
    { name: 'Frankfurt', lat: 50.110, lng: 8.682 },
    { name: 'Stuttgart', lat: 48.776, lng: 9.183 },
    { name: 'Düsseldorf', lat: 51.228, lng: 6.773 },
    { name: 'Leipzig', lat: 51.341, lng: 12.375 },
    { name: 'Dresden', lat: 51.051, lng: 13.738 },
    { name: 'Hannover', lat: 52.376, lng: 9.732 },
    { name: 'Freiburg', lat: 47.999, lng: 7.842 },
    { name: 'Luxemburg', lat: 49.612, lng: 6.131 }
  ]
};
